import axios, {AxiosInstance} from "axios";
import IAuthResponse from "@/types/Api/IAuthResponse";
import {Token} from "@/types/Auth/Token";
import {useAuthStore} from "@/stores/Auth";
import {Config} from "@/models/settings/Config";
import router from "@/router";
import ServiceBase from "@/services/ServiceBase";

class ApiService extends ServiceBase {
    private static instance: ApiService;
    private readonly baseUrl!: string;
    public readonly httpClient: AxiosInstance;

    private isRefreshing = false;

    private constructor() {
        super();
        this.baseUrl = Config.getApiEndpoint();
        console.log("Using API baseUrl: ", this.baseUrl);
        this.httpClient = axios.create({
            baseURL: this.baseUrl,
            withCredentials: true,
        });

        this.httpClient.defaults.headers.common["Content-Type"] = "application/json";

        this.httpClient.interceptors.response.use(
            (response) => {
                //TODO: add auth header here?
                return response;
            },
            async (error) => {
                const originalRequest = error.config;
                console.log("interceptor error");
                console.log("url: ", originalRequest.url);
                console.log("original request", originalRequest);
                console.log("error: ", error);
                /*----- network error - likely due to maintenance work -----*/
                if (error.code === "ERR_NETWORK") {
                    console.log("ApiService - ERR_NETWORK");
                    console.log("error", error);
                    originalRequest._retry = false;
                    await router.push({name: "maintenance"});
                    return Promise.reject(error);
                    //return Promise.reject(error);
                }
                /*----- network error - likely due to maintenance work -> timeout -----*/
                if (error.code === "ECONNABORTED") {
                    originalRequest._retry = false;
                    console.log("ApiService - ECONNABORTED -> timeout");
                    await router.push({name: "maintenance"});
                    return Promise.reject(error);
                }

                /*----- token expired error -----*/
                if (error.response && error.response.headers["content-type"] === "Token-Expired" && !originalRequest._retry) {
                    if (!this.isRefreshing && originalRequest.url !== "auth/refresh-token") {
                        console.log("ERR_TOKEN_EXPIRED - refresh token");
                        originalRequest._retry = true;
                        this.isRefreshing = true;
                        console.table(error.response.headers);
                        console.log("ERR_TOKEN ERROR", error);
                        const authStore = useAuthStore();
                        const token: Token = {
                            accessToken: await authStore.getAccessToken(),
                            refreshToken: await authStore.getRefreshToken(),
                        };
                        console.log("current token: ", token);
                        if (token.accessToken == null || token.refreshToken == null) {
                            return;
                        }
                        if (!(await this.refreshToken())) {
                            console.log("refresh token failed");
                            originalRequest._retry = false;
                            this.isRefreshing = false;
                            await router.push({name: "welcome"});
                            return Promise.reject(error);
                        }
                        this.isRefreshing = false;
                        /*----- update request headers -----*/
                        const accessToken = await authStore.getAccessToken();
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

                        return this.httpClient(originalRequest);
                    } else {
                        return new Promise((resolve) => {
                            console.log("ApiService await refresh token");
                            const intervalId = setInterval(async () => {
                                if (!this.isRefreshing) {
                                    console.log("ApiService clear interval");
                                    /*----- update request headers -----*/
                                    const authStore = useAuthStore();
                                    const accessToken = await authStore.getAccessToken();
                                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                                    clearInterval(intervalId);
                                    resolve(this.httpClient(originalRequest));
                                }
                            });
                        });
                    }
                }
                return Promise.reject(error);
            },
        );
    }

    private async refreshToken(): Promise<boolean> {
        console.log("calling refreshToken()");
        try {
            const authStore = useAuthStore();
            const token: Token = {
                accessToken: await authStore.getAccessToken(),
                refreshToken: await authStore.getRefreshToken(),
            };
            const response = await this.httpClient.post<IAuthResponse>("auth/refresh-token", token);
            console.log("refresh-token response", response);
            if (!response.data.refreshToken || !response.data.accessToken) return false;

            console.log("new token: ", response.data);
            await authStore.setRefreshToken(response.data.refreshToken);
            await authStore.setAccessToken(response.data.accessToken);
            return true;
        } catch (error) {
            console.log("refresh token error", error);
            return false;
        }
    }

    public static getInstance(): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService();
        }
        return ApiService.instance;
    }
}

export default ApiService;
